// AddFlavorModal.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { showHUD } from './NotificationHUD';
import {
  X,
  Save,
  Apple,
  Citrus,
  Cookie,
  Coffee,
  Flame,
  Leaf,
  CircleDot,
  HelpCircle,
  Camera,
} from 'lucide-react';
import {
  FlavorRepository,
  SeriesRepository,
  BrandRepository,
  LocalDatabase,
  DbSeries,
  DbBrand,
  DbFlavor,
} from '../repositories/LocalDatabase';
import { db } from '../repositories/db';
import { normalizeString, toTitleCase } from '../utils/stringUtils';
import QrScannerModal from './QrScannerModal';


interface AddFlavorModalProps {
  onClose: () => void;
  onSaveSuccess: () => void;
  initialScannedText?: string;
}

const AXES_KEYS = [
  { key: 'sweetness', label: '甘さ' },
  { key: 'sourness', label: '酸味' },
  { key: 'coolness', label: '清涼感' },
  { key: 'fruitiness', label: '果実感' },
] as const;

type AxisKey = (typeof AXES_KEYS)[number]['key'];

const SERIES_ICONS: Record<string, React.ReactNode> = {
  's-fruit': <Apple size={18} />,
  's-fruit-sour': <Citrus size={18} />,
  's-dessert': <Cookie size={18} />,
  's-cafe': <Coffee size={18} />,
  's-spice': <Flame size={18} />,
  's-mint': <Leaf size={18} />,
  's-nut': <CircleDot size={18} />,
  's-other': <HelpCircle size={18} />,
};

export default function AddFlavorModal({
  onClose,
  onSaveSuccess,
  initialScannedText,
}: AddFlavorModalProps) {
  const navigate = useNavigate();
  const [isClosing, setIsClosing] = useState(false);
  const [brandName, setBrandName] = useState('');
  const [name, setName] = useState('');

  const handleClose = () => {
    const animationsEnabled = localStorage.getItem('animationsEnabled') !== 'false';
    if (!animationsEnabled) {
      onClose();
      return;
    }
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 500);
  };
  const [seriesId, setSeriesId] = useState('');
  const [description, setDescription] = useState('');
  const [seriesList, setSeriesList] = useState<DbSeries[]>([]);
  const [nicotine, setNicotine] = useState(true);

  const [existingBrands, setExistingBrands] = useState<DbBrand[]>([]);
  const [existingFlavors, setExistingFlavors] = useState<DbFlavor[]>([]);
  const [showBrandSuggestions, setShowBrandSuggestions] = useState(false);
  const [showFlavorSuggestions, setShowFlavorSuggestions] = useState(false);

  const [showScanner, setShowScanner] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [brandSelectMode, setBrandSelectMode] = useState<'existing' | 'new'>('existing');
  const [brandDuplicateAlert, setBrandDuplicateAlert] = useState<{
    isOpen: boolean;
    matchedBrandName: string;
  } | null>(null);

  const [duplicateAlert, setDuplicateAlert] = useState<{
    isOpen: boolean;
    brandName?: string;
    flavorName?: string;
    flavorId?: string;
  } | null>(null);

  const handleScan = async (decodedText: string) => {
    setShowScanner(false);
    
    // Check if it's a Recipe QR
    const mixMatch = decodedText.match(/(?:mixes|r)\/([a-zA-Z0-9_-]+)/);
    let isMixJson = false;
    try {
      const data = JSON.parse(decodedText);
      if (data && data.name && data.bowl && data.coal) isMixJson = true;
    } catch(e) {}
    
    if (mixMatch || isMixJson) {
      showHUD({ type: 'success', message: 'レシピQRを検出しました。\n登録画面へ移動します。' });
      onClose();
      navigate('/create-recipe', { state: { scannedText: decodedText } });
      return;
    }

    // Check if it's a URL with flavor ID
    const urlMatch = decodedText.match(/(?:flavors|f)\/([a-zA-Z0-9_-]+)/);
    if (urlMatch) {
      const flavorId = urlMatch[1];
      try {
        const flavor = await FlavorRepository.getById(flavorId);
        if (!flavor) {
          showHUD({ type: 'error', message: '指定されたフレーバーが見つかりません' });
          return;
        }
        
        // Auto fill
        setBrandName(flavor.brand_name);
        setName(flavor.name);
        if (flavor.series_id) {
          setSeriesId(flavor.series_id);
        }
        if (flavor.description) {
          setDescription(flavor.description);
        }
        setNicotine(flavor.nicotine);
        if (flavor.taste_vector) {
          setTasteVector({
            sweetness: flavor.taste_vector.sweetness ?? 3,
            sourness: flavor.taste_vector.sourness ?? 3,
            coolness: flavor.taste_vector.coolness ?? 3,
            fruitiness: flavor.taste_vector.fruitiness ?? 3,
          });
        }
        
        showHUD({ type: 'success', message: 'フレーバー情報を読み込みました' });
      } catch (e) {
        showHUD({ type: 'error', message: '読み込みに失敗しました' });
      }
      return;
    }

    // Check if it's a JSON string representing a flavor
    try {
      const data = JSON.parse(decodedText);
      if (data && (data.name || data.flavorName)) {
        const brand = data.brand || data.brandName || data.brand_name || '';
        const fName = data.name || data.flavorName || data.flavor_name || '';
        const series = data.series || data.seriesId || data.series_id || '';
        const desc = data.description || data.desc || '';
        const nic = data.nicotine !== undefined ? !!data.nicotine : true;
        
        const sweetness = data.sweetness ?? data.taste_vector?.sweetness ?? data.tasteVector?.sweetness ?? 3;
        const sourness = data.sourness ?? data.taste_vector?.sourness ?? data.tasteVector?.sourness ?? 3;
        const coolness = data.coolness ?? data.taste_vector?.coolness ?? data.tasteVector?.coolness ?? 3;
        const fruitiness = data.fruitiness ?? data.taste_vector?.fruitiness ?? data.tasteVector?.fruitiness ?? 3;

        setBrandName(brand);
        setName(fName);
        if (series) setSeriesId(series);
        if (desc) setDescription(desc);
        setNicotine(nic);
        setTasteVector({ sweetness, sourness, coolness, fruitiness });

        showHUD({ type: 'success', message: 'フレーバー情報を読み込みました' });
        return;
      }
    } catch (e) {
      // Ignore JSON parse error, fallback to invalid QR message
    }

    showHUD({ type: 'error', message: '無効なQRコードです' });
  };


  const [tasteVector, setTasteVector] = useState({
    sweetness: 3,
    sourness: 3,
    coolness: 3,
    fruitiness: 3,
  });

  useEffect(() => {
    SeriesRepository.getAll().then((list) => {
      setSeriesList(list);
      if (list.length > 0) setSeriesId(list[0].id);
    });
    BrandRepository.getAll().then(setExistingBrands);
    db.flavors.toArray().then(setExistingFlavors);

    if (initialScannedText) {
      setTimeout(() => {
        handleScan(initialScannedText);
      }, 300);
    }
  }, [initialScannedText]);

  const filteredBrands = existingBrands
    .filter((b) => {
      if (!brandName) return true;
      return b.name.toLowerCase().includes(brandName.toLowerCase());
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));

  const filteredFlavors = existingFlavors.filter((f) => {
    const brandMatch = existingBrands.find((br) => br.id === f.brand_id);
    const isBrandMatch =
      brandMatch &&
      normalizeString(brandMatch.name) === normalizeString(brandName);
    if (!isBrandMatch) return false;
    if (!name) return true;
    return f.name.toLowerCase().includes(name.toLowerCase());
  });

  const handleSliderChange = (key: AxisKey, value: number) => {
    setTasteVector((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandName.trim() || !name.trim()) return;

    const normBrand = normalizeString(brandName);
    const normFlavor = normalizeString(name);

    const matchBrand = existingBrands.find(
      (b) => normalizeString(b.name) === normBrand,
    );
    if (matchBrand) {
      const matchFlavor = existingFlavors.find(
        (f) =>
          f.brand_id === matchBrand.id &&
          normalizeString(f.name) === normFlavor,
      );
      if (matchFlavor) {
        setDuplicateAlert({
          isOpen: true,
          brandName: matchBrand.name,
          flavorName: matchFlavor.name,
          flavorId: matchFlavor.id,
        });
        return;
      }
    }

    // フォーマットルール: ブランド名・フレーバー名は各単語の先頭を大文字にする
    const formattedBrand = toTitleCase(brandName.trim());
    const formattedFlavor = toTitleCase(name.trim());

    await FlavorRepository.addCustomFlavor(
      formattedBrand,
      formattedFlavor,
      seriesId,
      description,
      tasteVector,
      nicotine,
    );
    showHUD({ type: 'success', message: 'フレーバーを登録しました' });
    onSaveSuccess();
    handleClose();
  };

  const animationsEnabled = localStorage.getItem('animationsEnabled') !== 'false';

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(26, 28, 31, 0.85)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
        animation: !animationsEnabled ? 'none' : (isClosing ? 'fadeOut 0.5s ease-in forwards' : 'fadeIn 0.5s ease-out'),
        padding: '20px',
      }}
    >
      <form
        onSubmit={handleSave}
        className="glass-panel"
        style={{
          animation: !animationsEnabled ? 'none' : (isClosing ? 'modalScaleDown 0.5s cubic-bezier(0.25, 1, 0.5, 1) forwards' : 'none'),
          width: '90%',
          maxWidth: '650px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--color-surface)',
          padding: '24px',
          position: 'relative',
          overflowY: 'auto',
          gap: '15px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(231, 228, 223, 0.1)',
            paddingBottom: '12px',
          }}
        >
          <h2
            style={{
              fontSize: 'var(--font-size-h2)',
              fontFamily: 'var(--font-heading-ja)',
              margin: 0,
              color: 'var(--color-accent)',
            }}
          >
            フレーバーを新規登録
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={() => {
                if (sessionStorage.getItem('camera_permission_granted') === 'true') {
                  setShowScanner(true);
                } else {
                  setShowPermissionModal(true);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: 'var(--color-surface)',
                border: '1px solid rgba(231, 228, 223, 0.2)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-caption)',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(231, 228, 223, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-surface)';
              }}
            >
              <Camera size={18} />
              QRスキャン
            </button>
            <button
              type="button"
              onClick={handleClose}
              style={{
                background: 'rgba(231, 228, 223, 0.1)',
                borderRadius: '50%',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>
          </div>

        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {/* Brand Name */}
          <div style={{ position: 'relative' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                marginBottom: '6px',
                color: 'var(--color-accent)',
                fontWeight: 'bold',
              }}
            >
              ブランド名 *
            </label>
            {brandSelectMode === 'existing' ? (
              <select
                required
                value={brandName}
                onChange={(e) => {
                  if (e.target.value === '__NEW__') {
                    setBrandSelectMode('new');
                    setBrandName('');
                  } else {
                    setBrandName(e.target.value);
                  }
                }}
                style={{
                  width: '100%',
                  backgroundColor: 'rgba(26, 28, 31, 0.6)',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  borderRadius: '8px',
                  color: 'var(--color-text)',
                  padding: '10px',
                  fontSize: '14px',
                  outline: 'none',
                }}
              >
                <option value="" disabled>(ブランドを選択)</option>
                {[...existingBrands]
                  .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
                  .map((b) => (
                    <option key={b.id} value={b.name}>{b.name}</option>
                  ))}
                <option value="__NEW__">＋ 新規ブランドを追加...</option>
              </select>
            ) : (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  required
                  placeholder="例: Al Fakher, Social Smoke"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  onBlur={() => {
                    if (brandName.trim()) {
                      const match = existingBrands.find(b => b.name.toLowerCase() === brandName.trim().toLowerCase());
                      if (match) {
                        setBrandDuplicateAlert({ isOpen: true, matchedBrandName: match.name });
                      }
                    }
                  }}
                  style={{
                    flex: 1,
                    backgroundColor: 'rgba(26, 28, 31, 0.6)',
                    border: '1px solid rgba(231, 228, 223, 0.1)',
                    borderRadius: '8px',
                    color: 'var(--color-text)',
                    padding: '10px',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />
                <span
                  style={{
                    fontSize: '12px',
                    color: 'var(--color-accent)',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => {
                    setBrandSelectMode('existing');
                    setBrandName('');
                  }}
                >
                  既存から選ぶ
                </span>
              </div>
            )}
          </div>

          <div style={{ position: 'relative' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                marginBottom: '6px',
                color: 'var(--color-accent)',
                fontWeight: 'bold',
              }}
            >
              フレーバー名 *
            </label>
            <input
              type="text"
              required
              placeholder="例: Blueberry, Double Apple"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onFocus={() => setShowFlavorSuggestions(true)}
              onBlur={() =>
                setTimeout(() => setShowFlavorSuggestions(false), 200)
              }
              style={{
                width: '100%',
                backgroundColor: 'rgba(26, 28, 31, 0.6)',
                border: '1px solid rgba(231, 228, 223, 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                padding: '10px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            {showFlavorSuggestions && filteredFlavors.length > 0 && (
              <div
                className="hover-scrollbar"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 100,
                  marginTop: '4px',
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.4)',
                  maxHeight: '180px',
                }}
              >
                {filteredFlavors.map((f) => (
                  <div
                    key={f.id}
                    onMouseDown={() => {
                      setName(f.name);
                      setShowFlavorSuggestions(false);
                    }}
                    style={{
                      padding: '10px 14px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: 'var(--color-text)',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        'rgba(107, 78, 255, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {f.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Series */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                marginBottom: '8px',
                color: 'var(--color-accent)',
                fontWeight: 'bold',
              }}
            >
              系統を選択
            </label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gridAutoRows: '1fr',
                gap: '8px',
              }}
            >
              {seriesList.map((s) => {
                const isActive = seriesId === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSeriesId(s.id)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '10px',
                      borderRadius: '10px',
                      backgroundColor: isActive
                        ? 'var(--color-primary)'
                        : 'rgba(26, 28, 31, 0.4)',
                      border: `1px solid ${
                        isActive
                          ? 'var(--color-primary)'
                          : 'rgba(231, 228, 223, 0.1)'
                      }`,
                      color: 'var(--color-text)',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <span style={{ opacity: isActive ? 1 : 0.8 }}>
                      {SERIES_ICONS[s.id] || <HelpCircle size={18} />}
                    </span>
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: isActive ? 'bold' : 'normal',
                      }}
                    >
                      {s.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                marginBottom: '6px',
                color: 'var(--color-accent)',
                fontWeight: 'bold',
              }}
            >
              説明
            </label>
            <textarea
              placeholder="フレーバーの特徴や公式情報など..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: '100%',
                height: '70px',
                backgroundColor: 'rgba(26, 28, 31, 0.6)',
                border: '1px solid rgba(231, 228, 223, 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                padding: '10px',
                fontSize: '14px',
                resize: 'none',
                outline: 'none',
              }}
            />
          </div>

          {/* Nicotine Toggle */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                marginBottom: '6px',
                color: 'var(--color-accent)',
                fontWeight: 'bold',
              }}
            >
              ニコチン
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setNicotine(true)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  backgroundColor: nicotine
                    ? 'var(--color-primary)'
                    : 'rgba(231, 228, 223, 0.05)',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  color: 'var(--color-text)',
                  fontWeight: nicotine ? 'bold' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                あり
              </button>
              <button
                type="button"
                onClick={() => setNicotine(false)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '8px',
                  backgroundColor: !nicotine
                    ? 'var(--color-success)'
                    : 'rgba(231, 228, 223, 0.05)',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  color: 'var(--color-text)',
                  fontWeight: !nicotine ? 'bold' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                ノンニコチン
              </button>
            </div>
          </div>

          {/* Taste Vector */}
          <div>
            <span
              style={{
                display: 'block',
                fontSize: '14px',
                marginBottom: '12px',
                color: 'var(--color-accent)',
                fontWeight: 'bold',
              }}
            >
              フレーバー味覚（1〜5段階）
            </span>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '10px',
              }}
            >
              {AXES_KEYS.map(({ key, label }) => (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                  }}
                >
                  <span style={{ fontSize: '13px', width: '50px' }}>
                    {label}
                  </span>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    step="1"
                    value={tasteVector[key]}
                    onChange={(e) =>
                      handleSliderChange(key, parseInt(e.target.value))
                    }
                    style={{ flex: 1, accentColor: 'var(--color-primary)' }}
                  />
                  <span
                    style={{
                      fontSize: '13px',
                      width: '15px',
                      fontWeight: 'bold',
                    }}
                  >
                    {tasteVector[key]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '20px',
            borderTop: '1px solid rgba(231, 228, 223, 0.1)',
            paddingTop: '16px',
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              border: '1px solid rgba(231, 228, 223, 0.1)',
              color: 'var(--color-text)',
              fontSize: '14px',
            }}
          >
            キャンセル
          </button>
          <button
            type="submit"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 24px',
              borderRadius: '8px',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-text)',
              fontSize: '14px',
              boxShadow: '0 4px 15px rgba(107, 78, 255, 0.3)',
            }}
          >
            <Save size={16} />
            登録する
          </button>
        </div>
      </form>

      {duplicateAlert?.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(26, 28, 31, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 3000,
            animation: 'fadeIn 0.2s ease-out',
            padding: '20px',
          }}
        >
          <div
            className="glass-panel"
            style={{
              width: '90%',
              maxWidth: '400px',
              backgroundColor: 'var(--color-surface)',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              textAlign: 'center',
            }}
          >
            <h3
              style={{
                margin: 0,
                color: 'var(--color-accent)',
                fontSize: '18px',
                fontFamily: 'var(--font-heading-ja)',
              }}
            >
              重複の確認
            </h3>
            <p
              style={{
                color: 'var(--color-text)',
                fontSize: '14px',
                lineHeight: '1.6',
                margin: '10px 0',
              }}
            >
              すでに似た名前のフレーバー
              <br />
              <strong
                style={{ color: 'var(--color-primary)', fontSize: '16px' }}
              >
                {duplicateAlert.brandName} - {duplicateAlert.flavorName}
              </strong>
              <br />
              が登録されています。
              <br />
              既存のものを「所持フレーバー」に追加しますか？
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginTop: '10px',
              }}
            >
              <button
                type="button"
                onClick={() => setDuplicateAlert(null)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  color: 'var(--color-text)',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (duplicateAlert.flavorId) {
                    await FlavorRepository.saveUserFlavor(
                      duplicateAlert.flavorId,
                      true,
                      undefined,
                      undefined,
                      tasteVector,
                      nicotine,
                    );
                    showHUD({ type: 'success', message: '所持フレーバーに登録しました' });
                    setDuplicateAlert(null);
                    onSaveSuccess();
                    handleClose();
                  }
                }}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text)',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(107, 78, 255, 0.3)',
                  transition: 'all 0.2s',
                }}
              >
                追加する
              </button>
            </div>
          </div>
        </div>
      )}
      {showScanner && (
        <QrScannerModal
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
        />
      )}
      {showPermissionModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(26, 28, 31, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 3000,
            animation: 'fadeIn 0.2s ease-out',
            padding: '20px',
          }}
        >
          <div
            className="glass-panel"
            style={{
              width: '90%',
              maxWidth: '400px',
              backgroundColor: 'var(--color-surface)',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              textAlign: 'center',
            }}
          >
            <h3
              style={{
                margin: 0,
                color: 'var(--color-accent)',
                fontSize: '18px',
                fontFamily: 'var(--font-heading-ja)',
              }}
            >
              カメラのアクセス許可
            </h3>
            <p
              style={{
                color: 'var(--color-text)',
                fontSize: '14px',
                lineHeight: '1.6',
                margin: '10px 0',
              }}
            >
              QRコードをスキャンするためにカメラを起動します。
              <br />
              アクセスを許可しますか？
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginTop: '10px',
              }}
            >
              <button
                type="button"
                onClick={() => setShowPermissionModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(231, 228, 223, 0.1)',
                  color: 'var(--color-text)',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => {
                  sessionStorage.setItem('camera_permission_granted', 'true');
                  setShowPermissionModal(false);
                  setShowScanner(true);
                }}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text)',
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(107, 78, 255, 0.3)',
                  transition: 'all 0.2s',
                }}
              >
                許可する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

